import { useReportClock } from '../reportClock';
import { useState, useEffect, useMemo } from 'react';
import { AlertTriangle, Clock, DollarSign, FileText, Mail } from 'react-feather';
import IncomeCard from './IncomeCard';
import ProfileGreeting from './ProfileGreetingCard';
import GrowthOverview from './GrownCard';
import LatestActivityClass from './LatestActivityCard';
import TimelineCard from './TimelineCard';
import {
  formatDashboardCurrency,
  formatDashboardKpiNumber,
  formatEmailSyncWarningLabel,
  formatPreviousPeriodTrend,
  formatReportingFreshnessLabel,
  loadDashboardKpiData,
} from './activityData';
import {
  buildAgentLocationMarkerGroups,
  formatElapsedMinutes,
} from './agentLocationMapMarkers';
import { isAgentUidLike } from './agentLocationsData';
import { loadDashboardTrackingLocations } from './dashboardTrackingLocations';
import {
  CircleMarker,
  MapContainer,
  Popup,
  TileLayer,
  Tooltip,
  useMap,
} from 'react-leaflet';
import { Container, Row, Col } from 'reactstrap';
import 'leaflet/dist/leaflet.css';
import i18n from '../../../i18n';

const emptyIssuedTicketKpi = {
  todayTotal: 0,
  yesterdayTotal: 0,
  percentageChange: 0,
  last24hTotal: 0,
  previous24hTotal: 0,
  last24hPercentageChange: 0,
  currentSevenDayTotal: 0,
  previousSevenDayTotal: 0,
  sevenDayPercentageChange: 0,
};

const emptyDashboardKpis = {
  loading: true,
  issuedTickets: emptyIssuedTicketKpi,
  vehicleAlerts: {
    threshold: 5,
    totalVehicles: 0,
    totalEntries: 0,
    maxNoticeCount: 0,
    vehiclesOver10: 0,
    vehiclesOver25: 0,
    vehiclesOver50: 0,
  },
  contestations: {
    todayTotal: 0,
    yesterdayTotal: 0,
    percentageChange: 0,
    matchedNoticeCount: 0,
    last24hTotal: 0,
    previous24hTotal: 0,
    last24hPercentageChange: 0,
    last24hMatchedNoticeCount: 0,
  },
  paymentConfirmations: {
    todayTotal: 0,
    yesterdayTotal: 0,
    percentageChange: 0,
    todayAmount: 0,
    matchedNoticeCount: 0,
    missingAmountCount: 0,
    last24hTotal: 0,
    previous24hTotal: 0,
    last24hPercentageChange: 0,
    last24hAmount: 0,
    previous24hAmount: 0,
    last24hAmountPercentageChange: 0,
    last24hMatchedNoticeCount: 0,
    last24hMissingAmountCount: 0,
  },
  freshness: {
    latestReportingAt: '',
    ageMinutes: null,
    status: 'unknown',
    latestNoticeSyncAt: '',
    latestEmailSeenAt: '',
    latestEmailSyncAt: '',
    latestEmailSyncFailedAt: '',
    emailAgeMinutes: null,
    emailStatus: 'unknown',
    latestFirebaseProcessedAt: '',
    latestFailedSyncAt: '',
    oldestProcessingSyncAt: '',
    hasEmailSyncIssue: false,
    hasSyncIssue: false,
  },
};

const DEFAULT_MAP_CENTER = [45.508888, -73.561668];
const MAP_LAYERS = {
  satellite: {
    label: 'Satellite',
    attribution: 'Tiles &copy; Esri',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
  },
  plan: {
    label: 'Plan',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  },
};

const AgentLocationBounds = ({ agents }) => {
  const map = useMap();

  useEffect(() => {
    if (!agents.length) {
      map.setView(DEFAULT_MAP_CENTER, 10);
      return;
    }

    if (agents.length === 1) {
      map.setView([agents[0].latitude, agents[0].longitude], 13);
      return;
    }

    const bounds = agents.map(agent => [agent.latitude, agent.longitude]);
    map.fitBounds(bounds, {
      maxZoom: 13,
      padding: [30, 30],
    });
  }, [agents, map]);

  return null;
};

const AgentMapControls = ({ activeLayer, onLayerChange }) => {
  const map = useMap();

  const handleControlEvent = event => {
    event.stopPropagation();
  };

  const handleZoomIn = event => {
    handleControlEvent(event);
    map.zoomIn();
  };

  const handleZoomOut = event => {
    handleControlEvent(event);
    map.zoomOut();
  };

  const handleLayerChange = layerKey => event => {
    handleControlEvent(event);
    onLayerChange(layerKey);
  };

  return (
    <div
      className="maxpark-agent-map__controls"
      onClick={handleControlEvent}
      onDoubleClick={handleControlEvent}
      onMouseDown={handleControlEvent}
      onPointerDown={handleControlEvent}
    >
      <div className="maxpark-agent-map__zoom" aria-label="Controle de zoom carte">
        <button
          type="button"
          className="maxpark-agent-map__zoom-button"
          data-symbol="+"
          onClick={handleZoomIn}
          aria-label="Agrandir la carte"
          title="Agrandir"
        >
          <span className="maxpark-agent-map__zoom-symbol maxpark-agent-map__zoom-symbol--plus" aria-hidden="true" />
        </button>
        <button
          type="button"
          className="maxpark-agent-map__zoom-button"
          data-symbol="-"
          onClick={handleZoomOut}
          aria-label="Reduire la carte"
          title="Reduire"
        >
          <span className="maxpark-agent-map__zoom-symbol maxpark-agent-map__zoom-symbol--minus" aria-hidden="true" />
        </button>
      </div>
      <div className="maxpark-agent-map__layers" aria-label="Type de carte">
        {Object.entries(MAP_LAYERS).map(([layerKey, layer]) => (
          <button
            key={layerKey}
            type="button"
            className={`maxpark-agent-map__layer-button${activeLayer === layerKey ? ' is-active' : ''}`}
            data-label={layer.label}
            onClick={handleLayerChange(layerKey)}
            aria-pressed={activeLayer === layerKey}
          >
            <span className={`maxpark-agent-map__layer-icon maxpark-agent-map__layer-icon--${layerKey}`} aria-hidden="true" />
            <span className="maxpark-agent-map__layer-label">{layer.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

const getAgentMarkerRadius = group => (
  group.isCluster ? Math.min(20, 10 + group.agentCount * 2) : 8
);

const getAgentMarkerPathOptions = group => ({
  color: '#f0b429',
  fillColor: group.isCluster ? '#8f2d24' : '#e53935',
  fillOpacity: 0.88,
  weight: group.isCluster ? 3 : 2,
});

const isDisplayableAgentIdentity = value => Boolean(value) && !isAgentUidLike(value);

const getAgentIdentityLabel = agent => (
  [agent.agentName, agent.agentEmail, agent.agentId, agent.agentKey].find(isDisplayableAgentIdentity)
  || 'Agent MaxPark'
);

const getAgentDetailLabel = agent => {
  const identityLabel = getAgentIdentityLabel(agent);
  const details = [
    agent.agentEmail,
    agent.agentId,
    agent.agentKey,
  ].filter(value => isDisplayableAgentIdentity(value) && value !== identityLabel);

  return details[0] || '';
};

const AgentLocationPopupAgent = ({ agent }) => {
  const identityLabel = getAgentIdentityLabel(agent);
  const detailLabel = getAgentDetailLabel(agent);

  return (
    <div className="maxpark-agent-map__popup-agent">
      <div className="maxpark-agent-map__popup-agent-identity">
        <span>Agent</span>
        <strong>{identityLabel}</strong>
      </div>
      {detailLabel ? (
        <span>{detailLabel}</span>
      ) : null}
      <span>{agent.eventLabel || 'Position agent'}</span>
      {agent.ticketId ? (
        <span>Avis #{agent.ticketId}</span>
      ) : null}
      {agent.ageMinutes !== null ? (
        <span>Il y a {formatElapsedMinutes(agent.ageMinutes)}</span>
      ) : null}
    </div>
  );
};

const AgentLocationPopup = ({ group }) => {
  if (!group.isCluster) {
    return (
      <div className="maxpark-agent-map__popup-content">
        <AgentLocationPopupAgent agent={group.agents[0]} />
      </div>
    );
  }

  return (
    <div className="maxpark-agent-map__popup-content">
      <strong>{group.agentCount} agents a proximite</strong>
      <div className="maxpark-agent-map__popup-meta">
        Meme secteur GPS. Tous les agents sont listes ci-dessous.
      </div>
      <div className="maxpark-agent-map__popup-list">
        {group.agents.map(agent => (
          <AgentLocationPopupAgent key={agent.id} agent={agent} />
        ))}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const reportNow = useReportClock();
  const [dashboardKpis, setDashboardKpis] = useState(emptyDashboardKpis);
  const [kpiRefresh, setKpiRefresh] = useState(0);
  const [agentLocations, setAgentLocations] = useState({
    loading: true,
    agents: [],
    error: '',
  });
  const [agentMapLayer, setAgentMapLayer] = useState('satellite');

  const mapStyles = {
    width: '100%',
    height: '400px'
  };

  useEffect(() => {
    let isMounted = true;

    const loadDashboardKpis = async () => {
      try {
        const kpiData = await loadDashboardKpiData(new Date(), { skipCache: true });

        if (isMounted) {
          setDashboardKpis({
            ...emptyDashboardKpis,
            ...kpiData,
            loading: false,
          });
        }
      } catch (error) {
        if (isMounted) {
          setDashboardKpis({
            ...emptyDashboardKpis,
            loading: false,
            error: 'Les indicateurs sont indisponibles. Aucun total ne peut être confirmé pour le moment.',
          });
        }
      }
    };

    loadDashboardKpis();

    return () => {
      isMounted = false;
    };
  }, [kpiRefresh, reportNow]);

  useEffect(() => {
    let isMounted = true;
    let refreshTimer = null;

    const loadAgentLocations = async (options = {}) => {
      try {
        const report = await loadDashboardTrackingLocations({
          sinceHours: 24,
          limit: 250,
          ...options,
        });

        if (isMounted) {
          setAgentLocations({
            loading: false,
            agents: report.agents,
            error: '',
          });
        }
      } catch (error) {
        void error;

        if (isMounted) {
          setAgentLocations({
            loading: false,
            agents: [],
            error: 'Positions agents indisponibles.',
          });
        }
      }
    };

    loadAgentLocations({ skipCache: true });
    refreshTimer = window.setInterval(() => {
      loadAgentLocations({ skipCache: true });
    }, 60000);

    return () => {
      isMounted = false;
      if (refreshTimer) {
        window.clearInterval(refreshTimer);
      }
    };
  }, []);

  const issuedTicketKpi = dashboardKpis.issuedTickets || emptyIssuedTicketKpi;
  const vehicleAlertsKpi = dashboardKpis.vehicleAlerts || emptyDashboardKpis.vehicleAlerts;
  const contestationKpi = dashboardKpis.contestations || emptyDashboardKpis.contestations;
  const paymentConfirmationKpi = dashboardKpis.paymentConfirmations || emptyDashboardKpis.paymentConfirmations;
  const freshnessKpi = dashboardKpis.freshness || emptyDashboardKpis.freshness;
  const agentLocationMarkerGroups = useMemo(
    () => buildAgentLocationMarkerGroups(agentLocations.agents),
    [agentLocations.agents]
  );
  const kpisLoading = dashboardKpis.loading;

  const issuedTicketTrendSummary = kpisLoading
    ? { label: '...', trend: 'neutral', direction: 'neutral' }
    : formatPreviousPeriodTrend(
      issuedTicketKpi.last24hTotal,
      issuedTicketKpi.previous24hTotal,
      '24 h precedentes'
    );
  const issuedTicketAmount = kpisLoading ? '...' : formatDashboardKpiNumber(issuedTicketKpi.last24hTotal);
  const issuedTicketYesterdayAmount = kpisLoading ? '...' : formatDashboardKpiNumber(issuedTicketKpi.previous24hTotal);
  const issuedTicketPercent = issuedTicketTrendSummary.label;

  const vehicleAlertsAmount = kpisLoading ? '...' : formatDashboardKpiNumber(vehicleAlertsKpi.totalVehicles);
  const vehicleAlertsPercent = kpisLoading
    ? '...'
    : `${formatDashboardKpiNumber(vehicleAlertsKpi.totalEntries)} avis cumules`;
  const vehicleAlertsTrend = !kpisLoading && vehicleAlertsKpi.totalVehicles > 0 ? 'negative' : 'positive';

  const contestationTrendSummary = kpisLoading
    ? { label: '...', trend: 'neutral', direction: 'neutral' }
    : formatPreviousPeriodTrend(
      contestationKpi.last24hTotal,
      contestationKpi.previous24hTotal,
      '24 h precedentes',
      { lowerIsBetter: true }
    );
  const contestationAmount = kpisLoading ? '...' : formatDashboardKpiNumber(contestationKpi.last24hTotal);

  const paymentTrendSummary = kpisLoading
    ? { label: '...', trend: 'neutral', direction: 'neutral' }
    : formatPreviousPeriodTrend(
      paymentConfirmationKpi.last24hAmount,
      paymentConfirmationKpi.previous24hAmount,
      '24 h precedentes'
    );
  const paymentAmount = kpisLoading ? '...' : formatDashboardCurrency(paymentConfirmationKpi.last24hAmount);
  const paymentPercent = kpisLoading
    ? '...'
    : `${formatDashboardKpiNumber(paymentConfirmationKpi.last24hTotal)} avis pay\u00e9s sur 24 h`;

  const freshnessAmount = (() => {
    if (kpisLoading) {
      return '...';
    }

    if (freshnessKpi.status === 'fresh') {
      return 'OK';
    }

    if (freshnessKpi.status === 'idle') {
      return 'Aucun nouvel \u00e9v\u00e9nement';
    }

    if (freshnessKpi.status === 'sync_delayed') {
      return 'Sync en retard';
    }

    if (freshnessKpi.status === 'email_stale') {
      return 'Courriel en retard';
    }

    if (freshnessKpi.status === 'lagging' || freshnessKpi.status === 'stale') {
      return 'Retard';
    }

    return '--';
  })();
  const freshnessTrend = kpisLoading
    ? 'neutral'
    : freshnessKpi.status === 'fresh'
      ? 'positive'
      : freshnessKpi.status === 'unknown' || freshnessKpi.status === 'idle'
        ? 'neutral'
        : 'negative';
  const freshnessPercent = kpisLoading ? '...' : formatReportingFreshnessLabel(freshnessKpi);
  const emailSyncWarningLabel = kpisLoading ? '' : formatEmailSyncWarningLabel(freshnessKpi);


  return (
    <Container fluid={true} className="dashboard-default-sec">
      {dashboardKpis.error && <div role="alert" className="alert alert-warning">
        {dashboardKpis.error}
        <button type="button" className="btn btn-link" onClick={() => setKpiRefresh(value => value + 1)}>Réessayer</button>
      </div>}
      <Row>
        <Col xl="4" md="12" className="box-col-12 des-xl-100">
          <Row>
            <Col xl="12" md="6" className="">
              <ProfileGreeting />
              <TimelineCard />
            </Col>

          </Row>
        </Col >

        <Col md="12" xxl="8" className="order-8">

          {emailSyncWarningLabel ? (
            <Row>
              <Col md="12">
                <div className="maxpark-sync-warning" role="status">
                  <AlertTriangle aria-hidden="true" />
                  <div>
                    <strong>Courriels partages en retard</strong>
                    <span>{emailSyncWarningLabel}</span>
                  </div>
                </div>
              </Col>
            </Row>
          ) : null}

          <Row>
            <div className="maxpark-agent-map">
              <MapContainer
                center={DEFAULT_MAP_CENTER}
                zoom={10}
                scrollWheelZoom={false}
                zoomControl={false}
                style={mapStyles}
              >
                <TileLayer
                  key={agentMapLayer}
                  attribution={MAP_LAYERS[agentMapLayer].attribution}
                  url={MAP_LAYERS[agentMapLayer].url}
                />
                <AgentMapControls
                  activeLayer={agentMapLayer}
                  onLayerChange={setAgentMapLayer}
                />
                <AgentLocationBounds agents={agentLocationMarkerGroups} />
                {agentLocationMarkerGroups.map(group => (
                  <CircleMarker
                    key={group.id}
                    center={[group.latitude, group.longitude]}
                    radius={getAgentMarkerRadius(group)}
                    pathOptions={getAgentMarkerPathOptions(group)}
                  >
                    {group.isCluster ? (
                      <Tooltip
                        permanent
                        direction="center"
                        className="maxpark-agent-map__cluster-count"
                        opacity={1}
                      >
                        {group.agentCount}
                      </Tooltip>
                    ) : null}
                    <Popup className="maxpark-agent-map__popup">
                      <AgentLocationPopup group={group} />
                    </Popup>
                  </CircleMarker>
                ))}
              </MapContainer>
              {agentLocations.loading || agentLocations.error || agentLocations.agents.length === 0 ? (
                <div className="maxpark-agent-map__status">
                  {agentLocations.loading
                    ? 'Chargement des positions agents...'
                    : agentLocations.error || 'Aucune position agent recue pour les 24 dernieres heures.'}
                </div>
              ) : null}
            </div>
          </Row>

          <Row className="m-t-30">
            <Col md="12" xl="6" className="des-xl-50 box-col-12 activity-sec chart_data_left">
              <Row>
                <Col xl="6" md="3" sm="6" className="box-col-3 des-xl-25 rate-sec">
                  <IncomeCard
                    iconClass={<FileText />}
                    amount={dashboardKpis.error ? 'Indisponible' : issuedTicketAmount}
                    title={i18n.t('Dashboard_Card_IssuedTickets')}
                    primaryLabel={i18n.t('Dashboard_Card_Last24Hours')}
                    secondaryLabel={i18n.t('Dashboard_Card_Previous24Hours')}
                    secondaryAmount={dashboardKpis.error ? 'Indisponible' : issuedTicketYesterdayAmount}
                    percent={dashboardKpis.error ? '' : issuedTicketPercent}
                    trend={issuedTicketTrendSummary.trend}
                    trendDirection={issuedTicketTrendSummary.direction}
                  />
                </Col>
                <Col xl="6" md="3" sm="6" className="box-col-3 des-xl-25 rate-sec">
                  <IncomeCard
                    iconClass={<AlertTriangle />}
                    amount={dashboardKpis.error ? 'Indisponible' : vehicleAlertsAmount}
                    title={i18n.t('Dashboard_Card_VehicleWatch')}
                    percent={dashboardKpis.error ? '' : vehicleAlertsPercent}
                    trend={vehicleAlertsTrend}
                    href={`${process.env.PUBLIC_URL}/dashboard/alertes-vehicules`}
                  />
                </Col>
                <Col xl="6" md="3" sm="6" className="box-col-3 des-xl-25 rate-sec">
                  <IncomeCard
                    iconClass={<Mail />}
                    amount={dashboardKpis.error ? 'Indisponible' : contestationAmount}
                    title={i18n.t('Dashboard_Card_Contestations')}
                    percent={dashboardKpis.error ? '' : contestationTrendSummary.label}
                    trend={contestationTrendSummary.trend}
                    trendDirection={contestationTrendSummary.direction}
                  />
                </Col>
                <Col xl="6" md="3" sm="6" className="box-col-3 des-xl-25 rate-sec">
                  <IncomeCard
                    iconClass={<DollarSign />}
                    amount={dashboardKpis.error ? 'Indisponible' : paymentAmount}
                    title={i18n.t('Dashboard_Card_PaymentConfirmations')}
                    percent={dashboardKpis.error ? '' : paymentPercent}
                    trend={paymentTrendSummary.trend}
                    trendDirection={paymentTrendSummary.direction}
                  />
                </Col>
                <Col xl="6" md="3" sm="6" className="box-col-3 des-xl-25 rate-sec">
                  <IncomeCard
                    iconClass={<Clock />}
                    amount={dashboardKpis.error ? 'Indisponible' : freshnessAmount}
                    title={i18n.t('Dashboard_Card_ReportingFreshness')}
                    percent={dashboardKpis.error ? '' : freshnessPercent}
                    trend={freshnessTrend}
                  />
                </Col>
              </Row>


            </Col>
            <Col md="12" xl="6" className="">
              <LatestActivityClass />
            </Col>

          </Row>
        </Col>

      </Row>
    </Container>
  );
};

export default Dashboard;
